import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import BuyNowModal from "../components/BuyNowModal";
import JsonLd from "../components/JsonLd";
import { ShoppingCart, Star, Tag, CheckCircle2, ArrowLeft, ShieldCheck } from "lucide-react";

export default function CourseDetailPage() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const target = (slug || "").trim();
    if (!target) {
      setError("Course not found");
      setLoading(false);
      return;
    }

    api
      .get(`/courses/${encodeURIComponent(target)}`)
      .then((r) => {
        setCourse(r.data);
        if (r.data.meta_title) document.title = r.data.meta_title;
        const metaDesc = document.querySelector("meta[name=description]");
        if (metaDesc) metaDesc.setAttribute("content", r.data.meta_description || "");
        const metaKw = document.querySelector("meta[name=keywords]");
        if (metaKw) metaKw.setAttribute("content", r.data.meta_keywords || "");
        setLoading(false);
      })
      .catch((err) => {
        console.error("Course fetch error:", err);
        setError(err.response?.data?.detail || "Course not found");
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-32 text-center">
        <div className="inline-block w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mb-4" />
        <div className="text-slate-400 text-base">Loading course details...</div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-28 text-center">
        <div className="glass rounded-2xl p-8 border border-white/10">
          <h2 className="font-display font-bold text-2xl text-white mb-2">Course Not Found</h2>
          <p className="text-slate-400 text-sm mb-6">
            {error || "The course you are looking for might have been moved or updated."}
          </p>
          <Link
            to="/courses"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#E50914] to-[#B91C1C] text-white font-semibold text-sm hover:brightness-110 transition"
          >
            <ArrowLeft size={16} /> Explore All Courses
          </Link>
        </div>
      </div>
    );
  }

  const avgRating =
    course.reviews && course.reviews.length > 0
      ? course.reviews.reduce((s, r) => s + (r.rating || 5), 0) / course.reviews.length
      : 5;
  const priceNum = parseFloat(String(course.price).replace(/[^\d.]/g, "")) || 0;

  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: course.title,
    description: course.short_description || course.description,
    provider: {
      "@type": "EducationalOrganization",
      name: "Aducation",
      sameAs: typeof window !== "undefined" ? window.location.origin : "",
    },
    image: course.thumbnail_url,
    url: typeof window !== "undefined" ? window.location.href : "",
    inLanguage: ["en", "hi"],
    educationalCredentialAwarded: "Certificate of Completion",
    keywords: (course.tags || []).join(", "),
    ...(course.instructor
      ? { instructor: { "@type": "Person", name: course.instructor } }
      : {}),
    ...(course.reviews && course.reviews.length > 0
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: avgRating.toFixed(1),
            reviewCount: course.reviews.length,
            bestRating: 5,
            worstRating: 1,
          },
          review: course.reviews.slice(0, 5).map((r) => ({
            "@type": "Review",
            author: { "@type": "Person", name: r.name },
            reviewRating: { "@type": "Rating", ratingValue: r.rating || 5, bestRating: 5 },
            reviewBody: r.comment,
          })),
        }
      : {}),
    offers: {
      "@type": "Offer",
      price: priceNum,
      priceCurrency: "INR",
      category: "Paid",
      availability: "https://schema.org/InStock",
      url: typeof window !== "undefined" ? window.location.href : "",
    },
    hasCourseInstance: {
      "@type": "CourseInstance",
      courseMode: "online",
      inLanguage: ["en", "hi"],
    },
  };

  return (
    <div data-testid="course-detail-page">
      <JsonLd data={courseSchema} id="jsonld-course" />
      <section className="relative overflow-hidden">
        <div className="hero-glow absolute inset-0 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 relative">
          <Link to="/courses" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-6">
            <ArrowLeft size={14} /> Back to Courses
          </Link>

          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div className="aspect-[16/9] rounded-2xl overflow-hidden border border-white/10">
              <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover" />
            </div>

            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="red-pill text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
                  {course.category}
                </span>
                <span className="text-xs text-slate-400">By {course.instructor}</span>
              </div>
              <h1 className="font-display font-black text-3xl lg:text-5xl leading-tight tracking-tight">
                {course.title}
              </h1>
              <p className="mt-4 text-slate-300 text-lg leading-relaxed">{course.short_description}</p>

              <div className="flex items-center gap-3 mt-5">
                <div className="flex items-center gap-1 text-amber-400">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={16} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <span className="text-sm text-slate-400">({course.reviews?.length || 0}+ Verified Reviews)</span>
              </div>

              <div className="mt-6 flex items-baseline gap-3">
                <span className="text-slate-500 line-through text-lg">{course.original_price}</span>
                <span className="gold-text font-display font-black text-5xl">{course.price}</span>
                <span className="text-emerald-400 text-sm font-semibold">Limited Time Offer</span>
              </div>

              <button
                data-testid="detail-buy-now-btn"
                onClick={() => setModalOpen(true)}
                className="mt-6 w-full sm:w-auto inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-[#E50914] to-[#B91C1C] text-white font-bold btn-red-glow hover:brightness-110 transition text-lg"
              >
                <ShoppingCart size={20} /> Buy Now
              </button>

              <div className="mt-5 flex items-center gap-2 text-xs text-emerald-400">
                <ShieldCheck size={14} /> Access via Aducation Android & Windows App
              </div>

              {course.tags && course.tags.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {course.tags.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 text-xs text-slate-300 bg-white/5 border border-white/10 px-3 py-1.5 rounded-full">
                      <Tag size={11} /> {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <h2 className="font-display font-bold text-2xl lg:text-3xl mb-5">
              About this <span className="gold-text">Course</span>
            </h2>
            <div className="text-slate-300 leading-relaxed whitespace-pre-line">{course.description}</div>
          </div>

          <div className="glass rounded-2xl p-6 h-fit">
            <h3 className="font-display font-bold text-lg mb-4">What You Get</h3>
            <ul className="space-y-3 text-sm text-slate-300">
              {["Lifetime Access on App", "PDF Notes & MCQs", "Live Doubt Sessions", "Certificate on Completion", "Placement/Job Guidance"].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Reviews */}
      {course.reviews && course.reviews.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-display font-bold text-2xl lg:text-3xl mb-8">
            Student <span className="red-text">Reviews</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {course.reviews.map((r, i) => (
              <div key={i} className="glass rounded-2xl p-6">
                <div className="flex items-center gap-1 text-amber-400 mb-3">
                  {Array.from({ length: r.rating || 5 }).map((_, j) => (
                    <Star key={j} size={14} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">"{r.comment}"</p>
                <div className="mt-4">
                  <div className="text-sm font-semibold text-white">{r.name}</div>
                  {r.role && <div className="text-xs text-amber-400/80">{r.role}</div>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <BuyNowModal open={modalOpen} onClose={() => setModalOpen(false)} course={course} />
    </div>
  );
}
